/**
 * useAuditPlotPlantings — CRUD reactivo (via REST) de las
 * `AuditPlotPlanting` de un `AuditPlot` desde el web.
 *
 * Réplica funcional del hook homónimo de la app móvil
 * (mobile/src/hooks/useAuditPlotPlantings.ts). Mismo contrato de API
 * — addPlanting / updatePlanting / removePlanting / syncPrimaryFromParent
 * — pero apoyado en `/api/audit-plot-plantings/` en vez de la BD
 * local de WatermelonDB. Ver el hook móvil para la convención de
 * "plantación principal" (ordering=1) y "plantings extra"
 * (ordering >= 2).
 *
 * Sólo tiene sentido en edit-mode: la parcela DEBE existir en el
 * servidor para poder crear plantings asociados. En modo creación
 * hay que persistir primero la parcela y luego montar los plantings
 * (el UI del form gestiona la transición).
 */

import { useCallback, useEffect, useState } from "react";

import type { AuditPlotPlanting } from "@/types/audit";
import { auditPlotPlantingService } from "@/services/api/auditPlotPlantingService";

/** Payload de los campos legacy del `AuditPlot` que la planting
 *  principal (ordering=1) refleja. Se pasa a `syncPrimaryFromParent`
 *  al guardar la parcela. Mismos campos que en el hook móvil. */
export interface PrimaryPlantingSnapshot {
  variety: number | null;
  pattern: number | null;
  plot_type: number | null;
  intermediate_wood: number | null;
  crop_date: number | null;
  plantation_frame_row: number | null;
  plantation_frame_column: number | null;
  plants: number | null;
  grafting: number | null;
  surface: number | null;
}

/** Seed opcional al crear una planting nueva — típicamente heredamos
 *  variedad/patrón/tipo del padre para que el auditor no reelija en
 *  cada doblado (que casi siempre comparten esos campos). */
export interface PlantingSeed {
  variety?: number | null;
  pattern?: number | null;
  plot_type?: number | null;
  intermediate_wood?: number | null;
  crop_date?: number | null;
  plantation_frame_row?: number | null;
  plantation_frame_column?: number | null;
  plants?: number | null;
  grafting?: number | null;
  surface?: number | null;
}

export interface UseAuditPlotPlantingsResult {
  plantings: AuditPlotPlanting[];
  loading: boolean;
  refresh: () => Promise<void>;
  addPlanting: (seed?: PlantingSeed) => Promise<AuditPlotPlanting>;
  updatePlanting: (
    plantingId: string,
    patch: Partial<AuditPlotPlanting>,
  ) => Promise<AuditPlotPlanting>;
  removePlanting: (plantingId: string) => Promise<void>;
  syncPrimaryFromParent: (
    parent: PrimaryPlantingSnapshot,
  ) => Promise<void>;
}

export function useAuditPlotPlantings(
  auditPlotId: string | null,
): UseAuditPlotPlantingsResult {
  const [plantings, setPlantings] = useState<AuditPlotPlanting[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const refresh = useCallback(async () => {
    if (!auditPlotId) {
      setPlantings([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const rows = await auditPlotPlantingService.list(auditPlotId);
      const sorted = [...rows].sort(
        (a, b) => (a.ordering ?? 1) - (b.ordering ?? 1),
      );
      setPlantings(sorted);
    } finally {
      setLoading(false);
    }
  }, [auditPlotId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const addPlanting = useCallback(
    async (seed?: PlantingSeed): Promise<AuditPlotPlanting> => {
      if (!auditPlotId) throw new Error("Sin parcela auditada");
      // addPlanting siempre crea una plantación EXTRA (ordering ≥ 2).
      // La principal (ordering=1) la escribe syncPrimaryFromParent al
      // guardar la parcela. Si aún no hay plantings, arrancamos en 2
      // para no colisionar con la principal futura.
      const nextOrdering =
        plantings.length === 0
          ? 2
          : Math.max(
              2,
              Math.max(...plantings.map((p) => p.ordering ?? 1)) + 1,
            );
      const created = await auditPlotPlantingService.create({
        audit_plot: auditPlotId,
        ordering: nextOrdering,
        // variety es NOT NULL en el backend; el UI debe llenarlo antes
        // de guardar. Si el seed no lo trae, el backend rechaza y el
        // caller muestra el error — es más seguro que autoinventar.
        variety: (seed?.variety ?? null) as number,
        pattern: seed?.pattern ?? null,
        plot_type: seed?.plot_type ?? null,
        intermediate_wood: seed?.intermediate_wood ?? null,
        crop_date: seed?.crop_date ?? null,
        plantation_frame_row: seed?.plantation_frame_row ?? null,
        plantation_frame_column: seed?.plantation_frame_column ?? null,
        plants: seed?.plants ?? null,
        grafting: seed?.grafting ?? null,
        surface: seed?.surface ?? null,
      });
      await refresh();
      return created;
    },
    [auditPlotId, plantings, refresh],
  );

  const updatePlanting = useCallback(
    async (
      plantingId: string,
      patch: Partial<AuditPlotPlanting>,
    ): Promise<AuditPlotPlanting> => {
      const updated = await auditPlotPlantingService.update(plantingId, {
        // Solo enviamos los campos que el caller pasó explícitamente
        // — evita nullificar accidentalmente por omisión.
        ...("ordering" in patch ? { ordering: patch.ordering as number } : {}),
        ...("variety" in patch ? { variety: patch.variety as number } : {}),
        ...("pattern" in patch ? { pattern: patch.pattern ?? null } : {}),
        ...("plot_type" in patch
          ? { plot_type: patch.plot_type ?? null }
          : {}),
        ...("intermediate_wood" in patch
          ? { intermediate_wood: patch.intermediate_wood ?? null }
          : {}),
        ...("crop_date" in patch
          ? { crop_date: patch.crop_date ?? null }
          : {}),
        ...("plantation_frame_row" in patch
          ? { plantation_frame_row: patch.plantation_frame_row ?? null }
          : {}),
        ...("plantation_frame_column" in patch
          ? { plantation_frame_column: patch.plantation_frame_column ?? null }
          : {}),
        ...("plants" in patch ? { plants: patch.plants ?? null } : {}),
        ...("grafting" in patch ? { grafting: patch.grafting ?? null } : {}),
        ...("surface" in patch ? { surface: patch.surface ?? null } : {}),
        ...("sub_geom" in patch
          ? { sub_geom: patch.sub_geom ?? null }
          : {}),
      });
      await refresh();
      return updated;
    },
    [refresh],
  );

  const removePlanting = useCallback(
    async (plantingId: string) => {
      await auditPlotPlantingService.remove(plantingId);
      await refresh();
    },
    [refresh],
  );

  const syncPrimaryFromParent = useCallback(
    async (parent: PrimaryPlantingSnapshot) => {
      if (!auditPlotId) return;
      // Sin variedad no podemos crear la principal (variety NOT NULL);
      // el form ya valida que el padre tenga variety antes de guardar,
      // pero como último cinturón, salimos silenciosamente aquí para
      // no rebotar con un 400 confuso.
      if (parent.variety == null) return;
      const current = await auditPlotPlantingService.list(auditPlotId);
      const primary = current.find((p) => p.ordering === 1);
      const payload = {
        ordering: 1,
        variety: parent.variety,
        pattern: parent.pattern,
        plot_type: parent.plot_type,
        intermediate_wood: parent.intermediate_wood,
        crop_date: parent.crop_date,
        plantation_frame_row: parent.plantation_frame_row,
        plantation_frame_column: parent.plantation_frame_column,
        plants: parent.plants,
        grafting: parent.grafting,
        surface: parent.surface,
      };
      if (primary) {
        await auditPlotPlantingService.update(primary.id, payload);
      } else {
        await auditPlotPlantingService.create({
          audit_plot: auditPlotId,
          ...payload,
        });
      }
      await refresh();
    },
    [auditPlotId, refresh],
  );

  return {
    plantings,
    loading,
    refresh,
    addPlanting,
    updatePlanting,
    removePlanting,
    syncPrimaryFromParent,
  };
}
